import { dateDiff, timeUntil } from "../utils.date";
import React from "react";
import ServerManagerClient from "../services/server-manager.client";
import { ExtendedInfo, Server } from "../types/event/server";
import moment from "moment";
import { AgentStatusResponse } from "../types/AgentStatusResponse";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faCopy, faEdit, faInfo, faClose, faMailForward } from '@fortawesome/free-solid-svg-icons'
import { Col, Dropdown, DropdownButton, Modal, Nav, Row, Tab } from "react-bootstrap";

import '../css/dark-mode.css';
import { send } from "process";

interface Penalty {
  name: string;
  value: string;
  hasValue: boolean;
  values?: { key: string; value: string }[];
  freeText?: boolean;
}

const ListServers = () => {
  const sm = new ServerManagerClient();
  const interval = React.useRef<any>(null);
  const intExtended = React.useRef<any>(null);

  const [servers, setServers] = React.useState<Server[]>([]);
  
  const [selectedServer, setSelectedServer] = React.useState<Server | null>(null);
  const [extendedInfo, setExtendedInfo] = React.useState<ExtendedInfo | null>(null);

  const [agentStatus, setAgentStatus] = React.useState<AgentStatusResponse | null>(null);
  const [searchedServers, setSearchedServers] = React.useState<Server[]>([]);
  const [search, setSearch] = React.useState<string>("");
  const [penalty, setPenalty] = React.useState<Penalty | undefined>(undefined);
  const [penaltyDriver, setPenaltyDriver] = React.useState<string | undefined>(undefined);

  const pens: Penalty[] = [
    { name: "DQ", value: "/dq", hasValue: false },
    { name: "UNDQ", value: "/undq", hasValue: false },
    { name: "DNF", value: "/dnf", hasValue: false },
    { name: "UNDNF", value: "/undnf", hasValue: false },
    { name: "Add Pen", value: "/addpenalty", hasValue: true, freeText: false, 
      values: [
        { key: "drive-thru", value: "-1" },
        { key: "5s stop/go", value: "5" },
        { key: "10s stop/go", value: "10" },
        { key: "20s stop/go", value: "20" },
        { key: "60s stop/go", value: "60" },
      ],
    },
    {
      name: "Remove Pen",
      value: "/subpenalty",
      hasValue: true,
      values: [
        { key: "stop/go", value: "0" },
        { key: "drive-thru", value: "1" },
        { key: "all", value: "3" },
      ],
    },
    { name: "SetMass", value: "/allpens", hasValue: true, freeText: true },
    { name: "Change Laps", value: "/changelaps ", hasValue: true, freeText: true },
  ];

  const stop = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, server: Server) => {
    e.preventDefault();
    if (server.type == "event" && server.agentStatus === "Running") {
      alert("You cannot shutdown a running RACE server");
      return;
    }

    await sm.stop(server.taskArn)
      .then(() => {
        console.log(`Server ${server.taskArn} stopped`);
        close(e);
      })
      .catch((err: Error) => {
        console.error(err);
      });
  }

  const view = async (e: any, server: Server) => {
    e.preventDefault();
    setSelectedServer(server);

    sm.agentStatus(server).then((status: AgentStatusResponse) => {
      console.info(status);
      setAgentStatus(status);
    })
      .catch((err: Error) => {
        console.error(err);
      });
  }

  const close = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setSelectedServer(null);
  }

  const fetch = async () => {
    await sm.getServers()
      .then((servers: Server[]) => {
        setServers(servers);
        //setSearchedServers(find(servers, search));
        interval.current = setTimeout(() => fetch(), 5000);
      })
      .catch((err: Error) => {
        console.error(err);
        setServers([]);
        interval.current = setTimeout(() => fetch(), 30000);
      });
  }

  const fetchExtendedInfo = async (server: Server) => {
    await sm.getExtendedInfo(server)
      .then((extended: any) => {
        setExtendedInfo(extended);
        intExtended.current = setTimeout(() => { fetchExtendedInfo(server) }, 5000);
      })
      .catch((err: Error) => {
        console.error(err);
        intExtended.current = setTimeout(() => { fetchExtendedInfo(server) }, 10000);
      });
  }

  const uptime = (server: Server) => {
    if (!server) return "0s";

    const startedAt = moment(server.times.startedAt);
    const createdAt = moment(server.times.createdAt);

    if (!createdAt) return "0s";
    if (!startedAt) return "0s";

    if (startedAt.isBefore(createdAt)) {
      return moment(server.times.createdAt).fromNow();
    }

    return moment(server.times.startedAt).fromNow();
  }

  const killAll = async () => {
    if (!window.confirm("Are you REALLY sure you want to kill all the running servers?")) {
      console.log("Kill all servers command cancelled");
      return;

    } else if (!window.confirm("Are you REALLY REALLY sure you want to kill all servers?")) {
      console.log("Kill all servers command cancelled");
      return;
    }

    servers.forEach((server: Server) => {
      if (server.type == "event" && server.agentStatus === "Running") return;

      sm.stop(server.taskArn)
        .then(() => {
          console.log(`Server ${server.taskArn} stopped`);
        })
        .catch((err: Error) => {
          console.error(err);
        });
    });

    setServers([]);
  }

  const timeoutHandle = React.useRef<NodeJS.Timeout | null>(null);
  const handleSearch = (search: string) => {
    setSearch(search)
  }

  const find = (servers: Server[], search: string) => {
    const term = search.toLowerCase();
    if (term.length === 0) return servers;

    const filtered = servers.filter((server: Server) =>
      server.taskPublicIP.toLowerCase().includes(term) ||
      server.name.toLowerCase().includes(term) ||
      server.game.toLowerCase().includes(term) ||
      server.type.toLowerCase().includes(term) ||
      server.track.toLowerCase().includes(term) ||
      server.taskArn.toLowerCase().includes(term) ||
      server.agentSession.toLowerCase().includes(term) ||
      server.lobbyCode.toLowerCase().includes(term)
    );

    console.info('ListServers.tsx: find: ', filtered.length);
    return filtered;
  }

  const send = (server: Server, driver: string, penalty: Penalty) => {
    if (penalty.hasValue) {
      if (penalty.freeText) {
        const valueElement = document.getElementById(`pen-value-${driver}`) as HTMLInputElement;
        const value = valueElement ? valueElement.value : null;
        if (value) {
          sm.chat(server, `${penalty.value} ${value} ${driver}`);
        }

      } else {
        const valueElement = document.getElementById(`pen-value-${driver}`) as HTMLSelectElement;
        const value = valueElement ? valueElement.value : null;
        if (value) {
          sm.chat(server, `${penalty.value} ${value} ${driver}`);
        }
      }
    } else {
      sm.chat(server, `${penalty.value} ${driver}`);
    }
  }

  React.useEffect(() => {
    if (search.length === 0) {
      setSearchedServers(servers);
    } else {
      setSearchedServers(find(servers, search));
    }
  }, [search, servers])


  React.useEffect(() => {
    if (!selectedServer) {
      clearTimeout(intExtended.current);
    }
  }, [searchedServers])

  React.useEffect(() => {
    console.info("ListServers.tsx: penalty: ", penalty);
  }, [penalty])

  React.useEffect(() => {
    console.info("ListServers.tsx: penaltyDriver: ", penaltyDriver);
  }, [penaltyDriver])

  React.useEffect(() => {
    fetch();
    return () => clearTimeout(interval.current);
  }, []);

  
  React.useEffect(() => {
    if (selectedServer) {
      fetchExtendedInfo(selectedServer);
    }
  }, [selectedServer]);

  return (
    <>
      <div>
        {servers && servers.length > 0 && (
          <>
            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              ALL <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.length}
              </div>
            </div>

            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              Practice <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "practice").length}
              </div>
            </div>

            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              Event <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "event").length}
              </div>
            </div>

            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              Hosted <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "hosted").length}
              </div>
            </div>

            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              PLAYERS <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.map(s => parseInt(s.currentPlayers)).reduce((a, b, i) => a + b, 0)}
              </div>
            </div>
            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              PRACTICE <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "practice").map(s => parseInt(s.currentPlayers)).reduce((a, b, i) => a + b, 0)}
              </div>
            </div>
            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              RACE <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "event").map(s => parseInt(s.currentPlayers)).reduce((a, b, i) => a + b, 0)}
              </div>
            </div>
            <div className="badge badge-danger inline" style={{ marginRight: "0.5rem" }}>
              HOSTED <div className="badge badge-warning inline" style={{ margin: "0.25rem" }}>
                {servers?.filter(s => s.type === "hosted").map(s => parseInt(s.currentPlayers)).reduce((a, b, i) => a + b, 0)}
              </div>
            </div>

            <input type="button" className="btn btn-danger" value="kill all" onClick={() => { killAll(); }}
              style={{ marginLeft: "10rem" }} />

            <input type="text" className="form-control" placeholder="Search" value={search} onChange={(e) => handleSearch(e.target.value)}
              style={{ width: "10rem", position: "absolute", right: "30px", top: "40px" }} />
          </>
        )}
      </div>

      <div className="tableFixHead small">
        <table className="table table-dark">
          <thead>
            <tr>
              <th></th>
              <th scope="col">Game</th>
              <th>Type</th>
              <th scope="col">Name</th>
              <th scope="col">Status</th>
              <th scope="col">Players</th>
              <th scope="col">Track</th>
              <th scope="col">Session</th>
              <th scope="col">Created</th>
              <th scope="col">Uptime</th>
            </tr>
          </thead>
          <tbody>
            {!searchedServers
              ? (<p>No Servers Running</p>)
              : searchedServers.sort((a, b) => (a.type + a.name).localeCompare(b.type + b.name)).map((server: Server) => (
                <tr key={server.taskArn} onClick={(e) => view(e, server)}>
                  <td>
                    {server.taskArn.startsWith("i-") && (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="w" height="25px" width="25px" version="2.0" focusable="false" aria-hidden="true" data-testid="awsc-logo" viewBox="0 0 29 17">
                        <path fill="white" d="M8.38 6.17a2.6 2.6 0 00.11.83c.08.232.18.456.3.67a.4.4 0 01.07.21.36.36 0 01-.18.28l-.59.39a.43.43 0 01-.24.08.38.38 0 01-.28-.13 2.38 2.38 0 01-.34-.43c-.09-.16-.18-.34-.28-.55a3.44 3.44 0 01-2.74 1.29 2.54 2.54 0 01-1.86-.67 2.36 2.36 0 01-.68-1.79 2.43 2.43 0 01.84-1.92 3.43 3.43 0 012.29-.72 6.75 6.75 0 011 .07c.35.05.7.12 1.07.2V3.3a2.06 2.06 0 00-.44-1.49 2.12 2.12 0 00-1.52-.43 4.4 4.4 0 00-1 .12 6.85 6.85 0 00-1 .32l-.33.12h-.14c-.14 0-.2-.1-.2-.29v-.46A.62.62 0 012.3.87a.78.78 0 01.27-.2A6 6 0 013.74.25 5.7 5.7 0 015.19.07a3.37 3.37 0 012.44.76 3 3 0 01.77 2.29l-.02 3.05zM4.6 7.59a3 3 0 001-.17 2 2 0 00.88-.6 1.36 1.36 0 00.32-.59 3.18 3.18 0 00.09-.81V5A7.52 7.52 0 006 4.87h-.88a2.13 2.13 0 00-1.38.37 1.3 1.3 0 00-.46 1.08 1.3 1.3 0 00.34 1c.278.216.63.313.98.27zm7.49 1a.56.56 0 01-.36-.09.73.73 0 01-.2-.37L9.35.93a1.39 1.39 0 01-.08-.38c0-.15.07-.23.22-.23h.92a.56.56 0 01.36.09.74.74 0 01.19.37L12.53 7 14 .79a.61.61 0 01.18-.37.59.59 0 01.37-.09h.75a.62.62 0 01.38.09.74.74 0 01.18.37L17.31 7 18.92.76a.74.74 0 01.19-.37.56.56 0 01.36-.09h.87a.21.21 0 01.23.23 1 1 0 010 .15s0 .13-.06.23l-2.26 7.2a.74.74 0 01-.19.37.6.6 0 01-.36.09h-.8a.53.53 0 01-.37-.1.64.64 0 01-.18-.37l-1.45-6-1.44 6a.64.64 0 01-.18.37.55.55 0 01-.37.1l-.82.02zm12 .24a6.29 6.29 0 01-1.44-.16 4.21 4.21 0 01-1.07-.37.69.69 0 01-.29-.26.66.66 0 01-.06-.27V7.3c0-.19.07-.29.21-.29a.57.57 0 01.18 0l.23.1c.32.143.656.25 1 .32.365.08.737.12 1.11.12a2.47 2.47 0 001.36-.31 1 1 0 00.48-.88.88.88 0 00-.25-.65 2.29 2.29 0 00-.94-.49l-1.35-.43a2.83 2.83 0 01-1.49-.94 2.24 2.24 0 01-.47-1.36 2 2 0 01.25-1c.167-.3.395-.563.67-.77a3 3 0 011-.48A4.1 4.1 0 0124.4.08a4.4 4.4 0 01.62 0l.61.1.53.15.39.16c.105.062.2.14.28.23a.57.57 0 01.08.31v.44c0 .2-.07.3-.21.3a.92.92 0 01-.36-.12 4.35 4.35 0 00-1.8-.36 2.51 2.51 0 00-1.24.26.92.92 0 00-.44.84c0 .249.1.488.28.66.295.236.635.41 1 .51l1.32.42a2.88 2.88 0 011.44.9 2.1 2.1 0 01.43 1.31 2.38 2.38 0 01-.24 1.08 2.34 2.34 0 01-.68.82 3 3 0 01-1 .53 4.59 4.59 0 01-1.35.22l.03-.01z"></path>
                        <path fill="orange" d="M25.82 13.43a20.07 20.07 0 01-11.35 3.47A20.54 20.54 0 01.61 11.62c-.29-.26 0-.62.32-.42a27.81 27.81 0 0013.86 3.68 27.54 27.54 0 0010.58-2.16c.52-.22.96.34.45.71z"></path>
                        <path fill="orange" d="M27.1 12c-.4-.51-2.6-.24-3.59-.12-.3 0-.34-.23-.07-.42 1.75-1.23 4.63-.88 5-.46.37.42-.09 3.3-1.74 4.68-.25.21-.49.09-.38-.18.34-.95 1.17-3.02.78-3.5z"></path>
                      </svg>
                    )}
                    {server.taskArn.startsWith("sc-") && (<img src="https://www.servers.com/global/generated/assets/favicon.svg" height="25px" width="25px" />)}
                    {server.taskArn.startsWith("pg-") && (<img src="https://panel.gpcore.io/favicon.png" height="25px" width="25px" />)}
                  </td>
                  <td scope="row">{server.game}</td>
                  <td>{server.type}</td>
                  <td scope="row">{server.name}</td>
                  <td>{server.agentStatus} / {server.currentStatus}</td>
                  <td>{server.currentPlayers ?? 0} / {server.maxPlayers ?? 0}</td>
                  <td>{server.track}</td>
                  <td>{server.agentSession}</td>
                  <td>{moment(server.times.createdAt).format("DD/MM HH:mm:ss")}</td>
                  <td>{uptime(server)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {selectedServer && (
        <Modal show={selectedServer != null} onHide={() => setSelectedServer(null)} size="xl" className='modal-dark'>
          <Modal.Header closeButton className='modal-header-dark'>
            <Modal.Title>
              {selectedServer.taskArn.startsWith("i-")
                ? (<svg xmlns="http://www.w3.org/2000/svg" fill="w" height="50px" width="50px" version="2.0" focusable="false" aria-hidden="true" data-testid="awsc-logo" viewBox="0 0 29 17">
                  <path fill="white" d="M8.38 6.17a2.6 2.6 0 00.11.83c.08.232.18.456.3.67a.4.4 0 01.07.21.36.36 0 01-.18.28l-.59.39a.43.43 0 01-.24.08.38.38 0 01-.28-.13 2.38 2.38 0 01-.34-.43c-.09-.16-.18-.34-.28-.55a3.44 3.44 0 01-2.74 1.29 2.54 2.54 0 01-1.86-.67 2.36 2.36 0 01-.68-1.79 2.43 2.43 0 01.84-1.92 3.43 3.43 0 012.29-.72 6.75 6.75 0 011 .07c.35.05.7.12 1.07.2V3.3a2.06 2.06 0 00-.44-1.49 2.12 2.12 0 00-1.52-.43 4.4 4.4 0 00-1 .12 6.85 6.85 0 00-1 .32l-.33.12h-.14c-.14 0-.2-.1-.2-.29v-.46A.62.62 0 012.3.87a.78.78 0 01.27-.2A6 6 0 013.74.25 5.7 5.7 0 015.19.07a3.37 3.37 0 012.44.76 3 3 0 01.77 2.29l-.02 3.05zM4.6 7.59a3 3 0 001-.17 2 2 0 00.88-.6 1.36 1.36 0 00.32-.59 3.18 3.18 0 00.09-.81V5A7.52 7.52 0 006 4.87h-.88a2.13 2.13 0 00-1.38.37 1.3 1.3 0 00-.46 1.08 1.3 1.3 0 00.34 1c.278.216.63.313.98.27zm7.49 1a.56.56 0 01-.36-.09.73.73 0 01-.2-.37L9.35.93a1.39 1.39 0 01-.08-.38c0-.15.07-.23.22-.23h.92a.56.56 0 01.36.09.74.74 0 01.19.37L12.53 7 14 .79a.61.61 0 01.18-.37.59.59 0 01.37-.09h.75a.62.62 0 01.38.09.74.74 0 01.18.37L17.31 7 18.92.76a.74.74 0 01.19-.37.56.56 0 01.36-.09h.87a.21.21 0 01.23.23 1 1 0 010 .15s0 .13-.06.23l-2.26 7.2a.74.74 0 01-.19.37.6.6 0 01-.36.09h-.8a.53.53 0 01-.37-.1.64.64 0 01-.18-.37l-1.45-6-1.44 6a.64.64 0 01-.18.37.55.55 0 01-.37.1l-.82.02zm12 .24a6.29 6.29 0 01-1.44-.16 4.21 4.21 0 01-1.07-.37.69.69 0 01-.29-.26.66.66 0 01-.06-.27V7.3c0-.19.07-.29.21-.29a.57.57 0 01.18 0l.23.1c.32.143.656.25 1 .32.365.08.737.12 1.11.12a2.47 2.47 0 001.36-.31 1 1 0 00.48-.88.88.88 0 00-.25-.65 2.29 2.29 0 00-.94-.49l-1.35-.43a2.83 2.83 0 01-1.49-.94 2.24 2.24 0 01-.47-1.36 2 2 0 01.25-1c.167-.3.395-.563.67-.77a3 3 0 011-.48A4.1 4.1 0 0124.4.08a4.4 4.4 0 01.62 0l.61.1.53.15.39.16c.105.062.2.14.28.23a.57.57 0 01.08.31v.44c0 .2-.07.3-.21.3a.92.92 0 01-.36-.12 4.35 4.35 0 00-1.8-.36 2.51 2.51 0 00-1.24.26.92.92 0 00-.44.84c0 .249.1.488.28.66.295.236.635.41 1 .51l1.32.42a2.88 2.88 0 011.44.9 2.1 2.1 0 01.43 1.31 2.38 2.38 0 01-.24 1.08 2.34 2.34 0 01-.68.82 3 3 0 01-1 .53 4.59 4.59 0 01-1.35.22l.03-.01z"></path>
                  <path fill="orange" d="M25.82 13.43a20.07 20.07 0 01-11.35 3.47A20.54 20.54 0 01.61 11.62c-.29-.26 0-.62.32-.42a27.81 27.81 0 0013.86 3.68 27.54 27.54 0 0010.58-2.16c.52-.22.96.34.45.71z"></path>
                  <path fill="orange" d="M27.1 12c-.4-.51-2.6-.24-3.59-.12-.3 0-.34-.23-.07-.42 1.75-1.23 4.63-.88 5-.46.37.42-.09 3.3-1.74 4.68-.25.21-.49.09-.38-.18.34-.95 1.17-3.02.78-3.5z"></path>
                </svg>)
                : (<img src="https://www.servers.com/global/generated/assets/favicon.svg" height="50px" width="50px" />)
              }
              <span style={{ marginLeft: "1rem" }}>{selectedServer?.name}</span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className='modal-body-dark'>
            <h3>Server: {selectedServer.name}</h3>
            <Tab.Container defaultActiveKey="overview">
              <Nav variant="pills" className="flex">
                <Nav.Item>
                  <Nav.Link eventKey="overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="extended">Extended</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="timeline">Timeline</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="overview">
                  <div className="inspect-server">
                    <ul>
                      <li>
                        <span>TaskArn:</span> {selectedServer.taskArn} 
                        <button className="plain-button" onClick={() => navigator.clipboard.writeText(selectedServer.taskArn)}>
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </li>
                      <li>
                        <span>IP:</span> {selectedServer.taskPublicIP}
                        <button className="plain-button" onClick={() => navigator.clipboard.writeText(selectedServer.taskPublicIP)} style={{ background: "transparent", border: "none" }}>
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </li>
                      <li>
                        <span>Port:</span> {selectedServer.serverPort}
                        <button className="plain-button" onClick={() => navigator.clipboard.writeText(selectedServer.serverPort)} style={{ background: "transparent", border: "none" }}>
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </li>
                      <li>
                        <span>Password:</span> {selectedServer.password}
                        <button className="plain-button" onClick={() => navigator.clipboard.writeText(selectedServer.password)} style={{ background: "transparent", border: "none" }}>
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </li>
                      <li>
                        <span>Lobby Code:</span> {selectedServer.lobbyCode}
                        <button className="plain-button" onClick={() => navigator.clipboard.writeText(selectedServer.lobbyCode)} style={{ background: "transparent", border: "none" }}>
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </li>
                      <li><span>Agent Status:</span> {selectedServer.agentStatus}</li>
                      <li><span>SM Status:</span> {selectedServer.currentStatus}</li>
                      <li><span>Players:</span> {selectedServer.currentPlayers} / {selectedServer.maxPlayers}</li>
                      <li><span>Track:</span> {selectedServer.track}</li>
                      <li><span>Session:</span> {selectedServer.agentSession}</li>
                      <li><span>Created:</span> {moment(selectedServer.times.createdAt).format("ddd Do MMMM HH:mm:ss")}</li>
                      <li><span>Started:</span> {moment(selectedServer.times.startedAt).format("ddd Do MMMM HH:mm:ss")}</li>
                      <li><span>Boot Time:</span> {dateDiff(moment(selectedServer.times.createdAt).toDate(), moment(selectedServer.times.startedAt).toDate())} </li>
                      <li><span>Uptime:</span> {dateDiff(moment(selectedServer.times.startedAt).toDate(), moment().toDate())}</li>
                    </ul>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="extended">
                  <h3>Extended Info</h3>
                  <ul>
                    <li>Track: {extendedInfo?.track.name}</li>
                    <li>Cars: {extendedInfo?.cars?.length}</li>                    
                  </ul>

                  <h4>Drivers</h4>
                  
                  {extendedInfo?.drivers?.sort((a, b) => a.position - b.position).map((driver, index) => (
                    <Row key={driver.steamId} style={{ marginBottom: "1rem" }}>
                      <Col xs={7}>
                        <Row>
                          <Col xs={1}><span style={{ marginRight: "0.5rem" }}>{driver.position}</span></Col>
                          <Col xs={4}><span style={{ marginRight: "0.5rem" }}>{driver.name}</span></Col>
                          
                          <Col xs={1}><span style={{ marginRight: "0.5rem" }}>{driver.carClass}</span></Col>
                          <Col xs={5}><span style={{ marginRight: "0.5rem" }}>{driver.vehicleName}</span></Col>

                          <Col xs={1}>
                            <button className="plain-button" onClick={() => navigator.clipboard.writeText(driver.name)} style={{ background: "transparent", border: "none" }}>
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                          </Col>
                        </Row>
                      </Col>
                      
                      <Col xs={2}>
                      <select style={{ marginLeft: "1rem" }} 
                        onChange={(e) => { e.preventDefault(); setPenalty(pens.find(p => p.value === e.target.value));  setPenaltyDriver(driver.steamId); }} 
                        className="form-select">

                        <option value="">Select Penalty</option>
                        {pens.map((pen, index) => (
                          <option key={index} value={pen.value} 
                                  selected={penalty?.value === pen.value && penaltyDriver === driver.steamId}>
                            {pen.name}
                          </option>
                        ))}
                      </select>
                      </Col>

                      <Col>
                      {penalty && penalty.hasValue && !penalty.freeText && penaltyDriver === driver.steamId && (
                        <select className="form-select" style={{ marginLeft: "1rem" }} id={`pen-value-${driver.name}`}>
                          {penalty.values?.map((value, index) => (
                            <option key={index} value={value.value}>{value.key}</option>
                          ))}
                        </select>
                      )}

                      {penalty && penalty.freeText && penaltyDriver === driver.steamId && (
                        <input type="text" id={`pen-free-text-${driver.name}`} className="form-control" placeholder="Free Text" style={{ marginLeft: "1rem", width: "10rem" }} />
                      )}
                      </Col>

                      {penalty && penaltyDriver === driver.steamId && (
                        <Col>
                        <button className="btn btn-danger" style={{ marginLeft: "1rem" }} onClick={(e) => { send(selectedServer, driver.name, penalty); }}>
                          <FontAwesomeIcon icon={faMailForward} />
                        </button>
                        </Col>
                      )}
                    </Row>
                  ))}
                </Tab.Pane>

                <Tab.Pane eventKey="timeline">
                  <ul>
                    {agentStatus?.timeline.map((item, index) => (
                      <>
                        <li key={index}><strong>Time:</strong> {moment(item.timestamp).format("HH:mm:ss")}</li>
                        <li key={index}><strong>Duration:</strong> {item.duration ? item.duration : dateDiff(moment(item.timestamp).toDate(), moment().toDate())}</li>
                        <li key={index}><strong>Status:</strong>{item.Status}</li>
                        <li key={index}><strong>Action:</strong> {item.Action}</li>
                        <li key={index}><strong>Session:</strong> {item.Session}</li>
                        <hr />
                      </>
                    ))}
                  </ul>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <span>
              <a className="btn btn-success" href={`http://localhost:5397/rest/multiplayer/join?host=${selectedServer.taskPublicIP}&port=${selectedServer.serverPort}&password=${selectedServer.configuration.password}`}>Join Server</a>
            </span>
            <span style={{ position: "absolute", top: 0, right: 0 }}>
              <button className="btn" style={{ color: "white" }} onClick={close}><FontAwesomeIcon icon={faClose} /></button>
            </span>
            <span>
              <button className="btn btn-danger" onClick={(e) => stop(e, selectedServer)}>Stop Server</button>
            </span>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default ListServers;
